import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TrendingUp, Check } from 'lucide-react';
import ConfirmModal from '../components/Common/ConfirmModal';
import './EmployeeServices.scss';

interface CV {
  id: number;
  title: string;
  is_primary: boolean;
  boost_points?: number;
  boost_expires_at?: string;
}

// Pricing for CV boost packages
const BOOST_PACKAGES = [
  { id: 1, days: 7, points: 10, price: 4.99, popular: false },
  { id: 2, days: 14, points: 25, price: 8.99, popular: true },
  { id: 3, days: 30, points: 60, price: 14.99, popular: false },
];

// Custom plan rate
const CUSTOM_RATE = 0.50; // € per day

const EmployeeServices: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();
  
  const [cvs, setCvs] = useState<CV[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCvId, setSelectedCvId] = useState<number | null>(null);
  
  // Custom plan state
  const [customDays, setCustomDays] = useState(0);
  
  // Confirm modal state (for validation messages)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  
  // Calculate custom plan price
  const customPlanPrice = useMemo(() => {
    return customDays * CUSTOM_RATE;
  }, [customDays]);

  useEffect(() => {
    if (!isLoading && (!isAuthenticated || (user?.user_type !== 'employee' && user?.user_type !== 'job_seeker'))) {
      navigate('/');
    }
  }, [isAuthenticated, user, navigate, isLoading]);

  useEffect(() => {
    if (isAuthenticated && (user?.user_type === 'employee' || user?.user_type === 'job_seeker')) {
      fetchCVs();
    }
  }, [isAuthenticated, user]);

  const fetchCVs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cv/my`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        const cvList = Array.isArray(result.data) ? result.data : (Array.isArray(result) ? result : []);
        setCvs(cvList);
        
        // Auto-select primary CV or first CV
        const primaryCV = cvList.find((cv: CV) => cv.is_primary);
        if (primaryCV) {
          setSelectedCvId(primaryCV.id);
        } else if (cvList.length > 0) {
          setSelectedCvId(cvList[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching CVs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchasePackage = (packageId: number) => {
    if (!selectedCvId) {
      setConfirmMessage('Prašome pasirinkti CV');
      setShowConfirmModal(true);
      return;
    }

    const selectedPackage = BOOST_PACKAGES.find(p => p.id === packageId);
    if (!selectedPackage) return;

    // Navigate to checkout with package data
    navigate('/checkout', {
      state: {
        cvBoost: {
          cvId: selectedCvId,
          cvTitle: cvs.find(cv => cv.id === selectedCvId)?.title,
          days: selectedPackage.days,
          points: selectedPackage.points,
          price: selectedPackage.price,
          type: 'package'
        }
      }
    });
  };

  const handlePurchaseCustom = () => {
    if (!selectedCvId) {
      setConfirmMessage('Prašome pasirinkti CV');
      setShowConfirmModal(true);
      return;
    }

    if (customDays === 0) {
      setConfirmMessage('Prašome pasirinkti dienų skaičių');
      setShowConfirmModal(true);
      return;
    }

    // Navigate to checkout with custom plan data
    navigate('/checkout', {
      state: {
        cvBoost: {
          cvId: selectedCvId,
          cvTitle: cvs.find(cv => cv.id === selectedCvId)?.title,
          days: customDays,
          points: customDays, // 1 point per day for custom
          price: customPlanPrice,
          type: 'custom'
        }
      }
    });
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
  };

  const getSelectedCV = () => {
    return cvs.find(cv => cv.id === selectedCvId);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT');
  };

  if (isLoading || loading) {
    return (
      <div className="employee-services-page">
        <div className="container">
          <div className="loading">Kraunama...</div>
        </div>
      </div>
    );
  }

  const selectedCV = getSelectedCV();

  return (
    <div className="employee-services-page">
      <div className="container">
        {/* Breadcrumbs */}
        <div className="breadcrumbs">
          <span onClick={() => navigate('/')}>Pradžia</span>
          <span className="separator">&gt;</span>
          <span className="current">Paslaugos</span>
        </div>

        {/* Header */}
        <div className="page-header">
          <h1>CV iškėlimas</h1>
          <p className="subtitle">Pakelkite savo CV paieškos rezultatuose ir būkite pastebėti darbdavių</p>
        </div>

        {/* CV Selector */}
        {cvs.length === 0 ? (
          <div className="no-cv-message">
            <p>Jūs dar neturite sukurto CV.</p>
            <button className="btn-primary" onClick={() => navigate('/cv/create')}>
              Sukurti CV
            </button>
          </div>
        ) : (
          <>
            <div className="cv-selector">
              <label>Pasirinkite CV:</label>
              <select 
                value={selectedCvId || ''} 
                onChange={(e) => setSelectedCvId(Number(e.target.value))}
              >
                {cvs.map(cv => (
                  <option key={cv.id} value={cv.id}>
                    {cv.title} {cv.is_primary ? '(Pagrindinis)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Current Boost Status */}
            {selectedCV && selectedCV.boost_expires_at && new Date(selectedCV.boost_expires_at) > new Date() && (
              <div className="current-boost">
                <TrendingUp size={20} />
                <span>
                  Jūsų CV šiuo metu iškeltas iki <strong>{formatDate(selectedCV.boost_expires_at)}</strong>
                </span>
              </div>
            )}

            {/* Boost Packages */}
            <div className="packages-section">
              <h2>Pasirinkite planą</h2>
              <div className="packages-grid">
                {BOOST_PACKAGES.map(pkg => (
                  <div key={pkg.id} className={`package-card ${pkg.popular ? 'popular' : ''}`}>
                    {pkg.popular && <div className="popular-badge">Populiariausias</div>}
                    <div className="package-days">{pkg.days} dienų</div>
                    <div className="package-price">{pkg.price.toFixed(2)} €</div>
                    <ul className="package-features">
                      <li><Check size={14} /> CV rodomas aukščiau</li>
                      <li><Check size={14} /> +{pkg.points} iškelimo taškų</li>
                      <li><Check size={14} /> Didesnis matomumas</li>
                    </ul>
                    <button 
                      className="btn-purchase"
                      onClick={() => handlePurchasePackage(pkg.id)}
                    >
                      Pirkti
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="section-divider">
              <span>arba sukurkite savo planą</span>
            </div>

            {/* Custom Plan */}
            <div className="custom-plan-section">
              <h3>Sukurkite savo planą</h3>
              <div className="custom-plan-builder">
                <div className="plan-row">
                  <div className="plan-label">
                    <span className="plan-name">CV iškėlimas</span>
                    <span className="plan-rate">{CUSTOM_RATE.toFixed(2)} €/d.</span>
                  </div>
                  <div className="plan-slider-wrap">
                    <input
                      type="range"
                      min="0"
                      max="90"
                      value={customDays}
                      onChange={(e) => setCustomDays(Number(e.target.value))}
                      className="plan-slider"
                    />
                    <span className="plan-value">{customDays} d.</span>
                  </div>
                  <div className="plan-price">
                    {customPlanPrice.toFixed(2)} €
                  </div>
                </div>

                <div className="plan-footer">
                  <div className="plan-total">
                    <span>Viso:</span>
                    <span className="total-amount">{customPlanPrice.toFixed(2)} €</span>
                  </div>
                  <button 
                    className="btn-checkout"
                    onClick={handlePurchaseCustom}
                    disabled={customDays === 0}
                  >
                    Pirkti
                  </button>
                </div>
              </div>
            </div>

            {/* How it works */}
            <div className="how-it-works">
              <h3>Kaip veikia CV iškėlimas?</h3>
              <div className="steps">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>Pasirinkite planą</h4>
                    <p>Išsirinkite jums tinkamą iškėlimo paketą</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Atlikite mokėjimą</h4>
                    <p>Saugiai apmokėkite pasirinktą paslaugą</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Būkite matomi</h4>
                    <p>Jūsų CV bus rodomas aukščiau darbdavių paieškose</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirm Modal - for validation messages */}
      <ConfirmModal
        isOpen={showConfirmModal}
        message={confirmMessage}
        confirmText="Gerai"
        cancelText="Uždaryti"
        onConfirm={handleCloseModal}
        onCancel={handleCloseModal}
      />
    </div>
  );
};

export default EmployeeServices;

